import React, { useState, useRef } from 'react';
import { sections } from '../../data/SurveyData';
import Splash from './screens/Splash';
import './index.css';
import { post } from '../../services/ApiService';
import ThankYou from './screens/Thankyou';

const Survey = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [showThankYou, setShowThankYou] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const tabRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Initialize formData with question text and structure for answers
  const [formData, setFormData] = useState(() => {
    const data = {};
    sections.forEach(section => {
      section.questions.forEach(question => {
        data[question.id] = {
          question: question.text,
          rating: '',
          comment: ''
        };
      });
    });
    return data;
  });

  const handleStartSurvey = () => {
    setShowSplash(false);
  };

  const handleInputChange = (e, field) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: {
        ...formData[name],
        [field]: value
      }
    });
  };

  // Check if current question is answered
  const isCurrentQuestionAnswered = () => {
    const currentQ = sections[currentSection].questions[currentQuestion];
    const currentData = formData[currentQ.id];
    
    if (currentQ.type === "rating") {
      return currentData.rating !== '';
    } else {
      return currentData.comment.trim() !== '';
    }
  };

  const handleNext = () => {
    // Check if current question is answered before proceeding
    if (!isCurrentQuestionAnswered()) {
      alert('Please answer the current question before proceeding.');
      return;
    }
    
    const currentSectionQuestions = sections[currentSection].questions;
    
    if (currentQuestion < currentSectionQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      setCurrentQuestion(0);
    }
    if (tabRef.current) {
      tabRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      setCurrentQuestion(sections[currentSection - 1].questions.length - 1);
    }
    if (tabRef.current) {
      tabRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const resetSurvey = () => {
    // Reset all state to initial values
    setShowSplash(true);
    setShowThankYou(false);
    setIsLoading(false);
    setCurrentSection(0);
    setCurrentQuestion(0);
    
    // Reset form data
    const resetData = {};
    sections.forEach(section => {
      section.questions.forEach(question => {
        resetData[question.id] = {
          question: question.text,
          rating: '',
          comment: ''
        };
      });
    });
    setFormData(resetData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if all questions are answered before submitting
    const allQuestionsAnswered = sections.every(section => 
      section.questions.every(question => {
        const data = formData[question.id];
        if (question.type === "rating") {
          return data.rating !== '';
        } else {
          return data.comment.trim() !== '';
        }
      })
    );
    
    if (!allQuestionsAnswered) {
      alert('Please answer all questions before submitting the survey.');
      return;
    }
    
    setIsLoading(true);
    try {
      const body = {
        responses: Object.keys(formData).map(key => ({
          question: formData[key].question,
          rating: formData[key].rating,
          comment: formData[key].comment
        }))
      };

      const res = await post("api/forms/survey/submit", body);
      setIsLoading(false);
      if(res.success){
        setShowThankYou(true);
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      alert('There was an error submitting your survey. Please try again.');
    }
  };

  const isLastQuestion = currentQuestion === sections[currentSection].questions.length - 1;
  const isLastSection = currentSection === sections.length - 1;
  const isFirstQuestion = currentQuestion === 0;
  const isFirstSection = currentSection === 0;

  const currentQ = sections[currentSection].questions[currentQuestion];
  const questionNumber = sections
    .slice(0, currentSection)
    .reduce((acc, section) => acc + section.questions.length, 0) + currentQuestion + 1;
  const totalQuestions = sections.reduce((acc, section) => acc + section.questions.length, 0);

  if (showSplash) {
    return <Splash onStart={handleStartSurvey} />;
  }
  if (showThankYou) {
    return <ThankYou />;
  }

  return (
    <div className="survey-container">
      <div className="survey-header">
        <h1 className='text-dark'>Staff Feedback Survey</h1>
      </div>

      <div className="progress-container">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(questionNumber / totalQuestions) * 100}%` }}
          ></div>
        </div>
        <div className="progress-text">
          Question {questionNumber} of {totalQuestions}
        </div>
        <div className="section-indicator">
          Section {currentSection + 1}: {sections[currentSection].title}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="survey-form">
        <div className="question-container">
          <div className="question-item" ref={tabRef}>
            <label className="question-text">{currentQ.text}</label>
            
            {currentQ.type === "rating" ? (
              <>
                <div className="rating-container">
                  <label className="rating-option na-option">
                    <input
                      type="radio"
                      name={currentQ.id}
                      value="N/A"
                      checked={formData[currentQ.id]?.rating === "N/A"}
                      onChange={(e) => handleInputChange(e, 'rating')}
                      required={currentQ.type === "rating"}
                    />
                    <span className="rating-number">N/A</span>
                  </label>
                  
                  {[...Array(10)].map((_, i) => {
                    const ratingValue = i + 1;
                    return (
                      <label key={ratingValue} className="rating-option">
                        <input
                          type="radio"
                          name={currentQ.id}
                          value={ratingValue}
                          checked={formData[currentQ.id]?.rating === String(ratingValue)}
                          onChange={(e) => handleInputChange(e, 'rating')}
                          required={currentQ.type === "rating"}
                        />
                        <span className="rating-number">{ratingValue}</span>
                      </label>
                    );
                  })}
                </div>
                
                <div className="comment-container mt-4">
                  <label className="comment-label">Additional comments (optional):</label>
                  <textarea
                    name={currentQ.id}
                    value={formData[currentQ.id]?.comment || ''}
                    onChange={(e) => handleInputChange(e, 'comment')}
                    className="text-input"
                    rows="3"
                    placeholder="Share any additional thoughts here..."
                  />
                </div>
              </>
            ) : (
              <textarea
                name={currentQ.id}
                value={formData[currentQ.id]?.comment || ''}
                onChange={(e) => handleInputChange(e, 'comment')}
                className="text-input"
                rows="5"
                required
                placeholder="Please share your thoughts here..."
              />
            )}
          </div>
        </div>

        <div className="navigation-buttons">
          <div className="nav-button-left">
            {!(isFirstQuestion && isFirstSection) && (
              <button type="button" onClick={handlePrev} className="nav-button prev-button">
                Previous
              </button>
            )}
          </div>
          
          <div className="nav-button-right">
            {!(isLastQuestion && isLastSection) ? (
              <button 
                type="button" 
                onClick={handleNext} 
                className="nav-button next-button"
                disabled={!isCurrentQuestionAnswered()}
              >
                Next
              </button>
            ) : (
              <button type="submit" className="nav-button submit-button" disabled={isLoading}>
                {isLoading ? "Please wait..." : "Submit"}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export { Survey };